// The Funding Engine's home (FEHome.dc.html at laptop width; no board draws it on a phone, so it
// follows PhoneWeek.dc.html's pieces, in the layout's order). src/products/funding-engine/home.js
// works out what it says; the sections follow the product's published home layout (layout.js), with
// AdminLayout's default where none is published:
//
//   news    Adam's announcement for The Funding Engine, while he has 1 up
//   next    their next live session: "Week 4 is live tonight", Join and Add to calendar
//   resume  the week to catch up on first, beside the session; with no session to come, the page's
//           lead: carry on, watch next, when the next week opens, the homework still to tick, or
//           the certificate once it's issued
//   weeks   their 6 weeks (fixed: it moves, never hides)
//   todo    still to tick from the latest week they've watched
//   help    "Stuck on anything?": WhatsApp while their intake runs, the questions line otherwise
//   res     Resources, with Opening later while anything is locked
//
// The product route draws the frame, the payment banner and the welcome checklist above this (it
// shows only to a running intake until Adam writes steps for everyone else). While staff view as a
// member, nothing here writes: the ticks are off, and Join and Add to calendar still work.
// Once any of The Funding Engine's recordings has Adam's own thumbnail (decision 47, 12 Oct), the
// week to watch (carry on, start, catch up first) shows its recording's 16:9 frame at the top of its
// card, and each of the 6 weeks starts with its frame: the thumbnail, or the grey panel with the
// week's date (product/recordingThumbs.js). Until then the page is the board's. 1 sign-file call for
// the page.
// Since 13 Oct every new live cohort takes this home too, in whichever look it has (the product-types
// plan): inside another look's frame, styles/live-cohort.css points The Funding Engine's colours and
// fonts at that look's, so The Funding Engine's own page is unchanged.
import { useRef, useState } from 'react'
import { useMember } from '../../app/member.jsx'
import { useNow } from '../../lib/clock.js'
import { Link } from '../../lib/router.jsx'
import { downloadIcs } from '../../lib/calendar.js'
import { useViewingAs, readOnlyControl } from '../../lib/viewAs.jsx'
import { useProductData } from '../../product/ProductData.jsx'
import { useLayout, LayoutSections } from '../../product/Layout.jsx'
import { LIVE_COHORT_HOME } from '../../product/layout.js'
import { ResourcesSection } from '../../product/Resources.jsx'
import { ProductAnnouncement } from '../../product/ProductAnnouncement.jsx'
import { productAnnouncement } from '../../product/model.js'
import { resourceTabs } from '../../product/release.js'
import { tickHomework } from '../../product/data.js'
import { ChatIcon, PlayFill, Tick, VideoIcon } from '../../product/Icons.jsx'
import { feHome, todoFor } from '../../products/funding-engine/home.js'
import { anyThumbnail, recordingThumb, weekThumb } from '../../product/recordingThumbs.js'
import { RecordingThumb, useRecordingThumbs } from '../../product/RecordingThumb.jsx'
import '../../styles/funding-engine.css'
// a new live cohort in any other look takes this home, drawn in its own look (the product-types plan)
import '../../styles/live-cohort.css'

const SAVE_FAILED = "That didn't save. Check your connection and try again."
// FEHome.dc.html: the session beside the week to catch up on, Still to tick beside Stuck on anything
const HERO_COLUMNS = 'minmax(0, 1fr) 400px'
const PAIR_COLUMNS = 'repeat(2, minmax(0, 1fr))'

function MailIcon({ size = 20 }) {
  return (
    <svg className="icon-line" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ strokeWidth: 1.75 }}>
      <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" />
    </svg>
  )
}

export default function FundingEngineHome({ product }) {
  const { data, patch } = useProductData()
  const { me, lib } = useMember()
  const nowMs = useNow()
  const viewingAs = useViewingAs()
  const sections = useLayout(product, 'home', LIVE_COHORT_HOME)
  // which homework Still to tick lists is fixed when the page opens, so ticking doesn't move it
  const keep = useRef(null)
  const heroShown = Boolean(sections) && sections.some(s => s.key === 'next' && s.shown)
  const home = feHome(data, product, nowMs, { heroShown })
  // the recordings' frames, once any has a thumbnail: the lead's and each week's, signed together
  const any = anyThumbnail(data.recordings)
  const leadRec = home.lead?.recordingId ? (data.recordings || []).find(r => r.id === home.lead.recordingId) : null
  const leadThumb = leadRec ? recordingThumb(data, leadRec, any) : null
  const weekThumbs = Object.fromEntries(home.weeks.rows.map(r => [r.id, weekThumb(data, r.id, any)]))
  const thumbs = useRecordingThumbs([leadThumb, ...Object.values(weekThumbs)])

  if (!sections) return <div className="fe-home fe-home--loading" aria-busy="true" />

  if (keep.current === null) {
    const first = todoFor(data)
    keep.current = first ? { moduleId: first.moduleId, itemIds: first.items.map(h => h.id) } : false
  }
  const todo = keep.current ? todoFor(data, keep.current) : null
  const announcement = productAnnouncement(lib.data, product.id, nowMs)
  const hasResources = resourceTabs(data, nowMs).length > 0
  const leadHeading = home.lead && !home.heroShown

  const parts = {
    news: { place: 'full', when: Boolean(announcement), render: () => <ProductAnnouncement announcement={announcement} /> },
    next: { place: 'main', columns: HERO_COLUMNS, when: Boolean(home.hero), render: () => <Hero hero={home.hero} /> },
    resume: {
      place: 'side', columns: HERO_COLUMNS, when: Boolean(home.lead),
      render: () => <Lead lead={home.lead} heading={leadHeading} thumb={leadThumb} thumbs={thumbs} />,
    },
    weeks: { place: 'full', render: () => <Weeks weeks={home.weeks} frames={any ? weekThumbs : null} thumbs={thumbs} /> },
    todo: {
      place: 'main', columns: PAIR_COLUMNS, when: Boolean(todo),
      render: () => <Todo todo={todo} data={data} patch={patch} personId={me.person.id} viewingAs={viewingAs} />,
    },
    help: { place: 'side', columns: PAIR_COLUMNS, render: () => <Help help={home.help} /> },
    res: { place: 'full', when: hasResources, render: () => <ResourcesSection product={product} /> },
  }

  return (
    <div className={`fe-home${leadHeading ? ' fe-home--lead' : ''}`}>
      {!home.heroShown && !home.lead && <h1 className="sr-only">{product.name}</h1>}
      <LayoutSections sections={sections} parts={parts} className="fe-home-layout" />
    </div>
  )
}

// The next live session (FEHome.dc.html's hero)
function Hero({ hero }) {
  const [added, setAdded] = useState(false)
  return (
    <section className="fe-hero" aria-labelledby="fe-home-h1">
      {hero.eyebrow && <div className="fe-eyebrow">{hero.eyebrow}</div>}
      <h1 className="fe-hero-h1" id="fe-home-h1">{hero.title}</h1>
      <p className="fe-hero-lede">{hero.lede}</p>
      {(hero.join || hero.calendar) && (
        <div className="fe-hero-actions">
          {hero.join && (hero.join.open
            ? <a className="fe-join fe-join--open" href={hero.join.href} target="_blank" rel="noopener noreferrer"><VideoIcon size={20} />{hero.join.label}</a>
            : <span className="fe-join"><VideoIcon size={20} />{hero.join.label}</span>)}
          {hero.calendar && (
            <button type="button" className="fe-cal" onClick={() => { downloadIcs(hero.calendar); setAdded(true) }}>
              {added ? 'Added to your calendar' : 'Add to calendar'}
            </button>
          )}
        </div>
      )}
    </section>
  )
}

// Where they are: "Catch up first" beside a session, the page's lead without one
function Lead({ lead, heading, thumb, thumbs }) {
  const Title = heading ? 'h1' : 'div'
  return (
    <section className={`${heading ? 'fe-lead fe-lead--main' : 'fe-lead'}${thumb ? ' fe-lead--thumb' : ''}`} aria-labelledby="fe-lead-title">
      <RecordingThumb card={thumb} thumbs={thumbs} className="fe-lead-thumb" />
      <div className="fe-eyebrow">{lead.kicker}</div>
      <Title className="fe-lead-title" id="fe-lead-title">{lead.title}</Title>
      {(lead.bar != null || lead.line) && (
        <div className="fe-lead-where">
          {lead.bar != null && <span className="fe-bar" aria-hidden="true"><span style={{ width: `${lead.bar}%` }} /></span>}
          {lead.line && <span className="fe-lead-line">{lead.line}</span>}
        </div>
      )}
      {lead.button && (
        <Link to={lead.button.href} className="fe-lead-btn">
          {lead.button.play && <PlayFill size={16} />}{lead.button.label}
        </Link>
      )}
    </section>
  )
}

// "Your 6 weeks", each week with its recording's frame once any recording has a thumbnail
function Weeks({ weeks, frames, thumbs }) {
  const list = `fe-wk-list${weeks.dates ? '' : ' fe-wk-list--nodates'}${frames ? ' fe-wk-list--thumbs' : ''}`
  return (
    <section className="fe-weeks" aria-labelledby="fe-weeks-h2">
      <h2 className="fe-h2" id="fe-weeks-h2">{weeks.heading}</h2>
      <div className={list}>
        {weeks.rows.map(r => (
          <div key={r.id} className={`fe-wk fe-wk--${r.tone}`}>
            {frames && <RecordingThumb card={frames[r.id]} thumbs={thumbs} className="fe-wk-thumb" />}
            <span className="fe-wk-head">
              <span className="fe-wk-label">{r.label}</span>
              {weeks.dates && <span className="fe-wk-date">{r.date || ''}</span>}
            </span>
            <span className="fe-wk-main">
              <span className="fe-wk-title">{r.title}</span>
              <span className="fe-wk-status">{r.done && <Tick size={16} />}{r.status}</span>
            </span>
            {r.href
              ? <Link to={r.href} className="fe-wk-open" aria-label={`Open ${r.label}`}>Open</Link>
              : <span className="fe-wk-action">{r.action || ''}</span>}
          </div>
        ))}
      </div>
    </section>
  )
}

// "Still to tick from Week 2": the ticks save as they go, as on the week page
function Todo({ todo, data, patch, personId, viewingAs }) {
  const [line, setLine] = useState(null)
  function toggle(h, done) {
    if (viewingAs) return
    setLine(null)
    patch(d => ({ ...d, ticks: done ? [...new Set([...d.ticks, h.id])] : d.ticks.filter(x => x !== h.id) }))
    tickHomework(personId, h.id, done).catch(e => {
      if (done && e?.code === '23505') return
      patch(d => ({ ...d, ticks: done ? d.ticks.filter(x => x !== h.id) : [...d.ticks, h.id] }))
      setLine(SAVE_FAILED)
    })
  }
  return (
    <section className="fe-card fe-todo" aria-labelledby="fe-todo-h">
      <h2 className="fe-h3" id="fe-todo-h">{todo.heading}</h2>
      {todo.items.map(h => {
        const done = data.ticks.includes(h.id)
        return (
          <label key={h.id} className={done ? 'fe-tick fe-tick--done' : 'fe-tick'}>
            <input type="checkbox" checked={done} onChange={e => toggle(h, e.target.checked)} {...readOnlyControl(viewingAs)} />
            <span className="fe-tick-text">{h.body}</span>
          </label>
        )
      })}
      {line && <p className="fe-msg" role="alert">{line}</p>}
    </section>
  )
}

// "Stuck on anything?"
function Help({ help }) {
  return (
    <section className="fe-card fe-card--fill fe-help" aria-labelledby="fe-help-h">
      <h2 className="fe-h3" id="fe-help-h">Stuck on anything?</h2>
      <p className="fe-help-text">{help.text}</p>
      {help.link && (help.kind === 'whatsapp'
        ? <a className="fe-help-link" href={help.link.href} target="_blank" rel="noopener noreferrer"><ChatIcon size={20} />{help.link.label}</a>
        : <a className="fe-help-link" href={help.link.href}><MailIcon />{help.link.label}</a>)}
    </section>
  )
}
